import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, getDocs, query, orderBy } from "firebase/firestore";

const AddStore = () => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    description: "",
    openTime: "",
    closeTime: "",
    imageUrl: "",
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [stores, setStores] = useState([]);

  // Fetch stores from Firestore
  useEffect(() => {
    const fetchStores = async () => {
      const storesRef = collection(db, "Stores");
      const q = query(storesRef, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      setStores(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchStores();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!formData.name || !formData.address || !formData.phone || !formData.openTime || !formData.closeTime || !formData.imageUrl) {
      setErrorMsg("Please fill in all required fields including image URL.");
      return;
    }

    try {
      setLoading(true);
      const storesRef = collection(db, "Stores");
      await addDoc(storesRef, {
        ...formData,
        createdAt: serverTimestamp(),
      });
      setSuccessMsg("Store added successfully!");
      setFormData({
        name: "",
        address: "",
        phone: "",
        email: "",
        description: "",
        openTime: "",
        closeTime: "",
        imageUrl: "",
      });
      // Refresh stores list
      const snapshot = await getDocs(query(storesRef, orderBy("createdAt", "desc")));
      setStores(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setShowModal(false);
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to add store. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Add Store Button */}
      <div className="flex justify-end mb-6">
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          Add Store
        </button>
      </div>

      {/* Stores List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stores.length > 0 ? (
          stores.map(store => (
            <div key={store.id} className="bg-white rounded-lg shadow p-4">
              {store.imageUrl && (
                <img
                  src={store.imageUrl}
                  alt={store.name}
                  className="w-full h-40 object-cover rounded mb-3"
                />
              )}
              <h3 className="text-xl font-semibold">{store.name}</h3>
              <p className="text-gray-600">{store.address}</p>
              <p className="text-gray-600">Phone: {store.phone}</p>
              {store.email && <p className="text-gray-600">Email: {store.email}</p>}
              {store.description && <p className="text-gray-500 mt-1">{store.description}</p>}
              <p className="text-gray-600 mt-1">
                Open: {store.openTime} | Close: {store.closeTime}
              </p>
            </div>
          ))
        ) : (
          <p className="text-gray-500 col-span-full">No stores added yet.</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-lg font-bold"
            >
              &times;
            </button>

            <h2 className="text-2xl font-bold mb-4 text-center">Add New Store</h2>

            {successMsg && <p className="bg-green-100 text-green-700 p-2 rounded mb-3">{successMsg}</p>}
            {errorMsg && <p className="bg-red-100 text-red-700 p-2 rounded mb-3">{errorMsg}</p>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Store Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  placeholder="Enter store name"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  placeholder="Enter store address"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  placeholder="Enter email (optional)"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  placeholder="Enter store description (optional)"
                ></textarea>
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Image URL *</label>
                <input
                  type="text"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  placeholder="Enter image URL for the store"
                />
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block font-medium text-gray-700 mb-1">Open Time *</label>
                  <input
                    type="time"
                    name="openTime"
                    value={formData.openTime}
                    onChange={handleChange}
                    className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  />
                </div>
                <div className="flex-1">
                  <label className="block font-medium text-gray-700 mb-1">Close Time *</label>
                  <input
                    type="time"
                    name="closeTime"
                    value={formData.closeTime}
                    onChange={handleChange}
                    className="w-full border rounded-lg p-2 focus:outline-none focus:ring focus:border-blue-400"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition"
              >
                {loading ? "Adding..." : "Add Store"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddStore;
